import type * as hast from 'hast';

import { BLOCK_IMAGES, type BlockImagesAlign, type BlockImagesData, math } from '@gitbook/doc-core';
import { Block, type Inline } from '@gitbook/slate';

import type {
    HastToGastEatNodesFn,
    HastToGastTransformContext,
    HastToGastTransformMap,
} from './types';
import {
    concatText,
    getBooleanAttribute,
    getClassNamesAttribute,
    getEnumAttribute,
    isWhitespace,
    sanitizeSpaces,
    trimNewlines,
} from './utils';

/**
 *  Images are div elements with no classnames
 *  Math are div or span elements with class "math" and "math-display".
 */
export const transformImagesOrMaths: HastToGastTransformMap = {
    div: (node, eat, context) => {
        if (isImages(node)) {
            return processImagesWrapper(node, eat, context);
        }

        return processMathElement(node);
    },
    span: processMathElement,
};

/**
 * Process an element that can be a math block or inline.
 */
export function processMathElement(node: hast.Element): Block[] | Inline[] | void {
    const mode = isMath(node);

    if (!mode) {
        return;
    }

    // We go into the children and extract the text ourselves, as it could be nested in
    // other HTML elements etc.
    const formula = trimNewlines(sanitizeSpaces(concatText(node)));

    // Empty formula are not valid math blocks
    if (!formula?.trim()) {
        return [];
    }

    return mode === 'display' ? [math.createMathBlock(formula)] : [math.createMathInline(formula)];
}

function isMath(node: hast.Element): 'inline' | 'display' | null {
    const classNames = getClassNamesAttribute(node.properties);

    if (classNames.includes('math-inline')) {
        return 'inline';
    }

    if (!classNames.includes('math')) {
        return null;
    }
    return classNames.includes('math-display') ? 'display' : 'inline';
}

function isImages(node: hast.Element): boolean {
    const { className } = node.properties || {};
    return Array.isArray(className) ? className.length === 0 : !className;
}

function isImageWrapperChild(node: hast.ElementContent): boolean {
    if (node.type === 'comment' || isWhitespace(node)) {
        return true;
    }

    if (node.type !== 'element') {
        return false;
    }

    if (node.tagName === 'img' || node.tagName === 'figure' || node.tagName === 'picture') {
        return true;
    }

    return (
        node.tagName === 'span' &&
        node.children.length > 0 &&
        node.children.every(isImageWrapperChild)
    );
}

/**
 * Process a <p> or <div> element that might be a wrapper around images.
 */
export async function processImagesWrapper(
    node: hast.Element,
    eat: HastToGastEatNodesFn,
    context: HastToGastTransformContext
) {
    const containsOnlyImages =
        node.children.length > 0 && node.children.every((child) => isImageWrapperChild(child));

    if (!containsOnlyImages) {
        return;
    }

    const blocks = await eat('blocks', node.children, {
        ...context,
        parseImagesAsBlocks: true,
    });
    // Each image comes pre-wrapped in its own `images` block; regroup them under this wrapper.
    const images = blocks.flatMap((block): Block[] =>
        block.type === BLOCK_IMAGES
            ? block.nodes.toArray().filter((node): node is Block => Block.isBlock(node))
            : [block]
    );
    if (images.length === 0) {
        return [];
    }

    const align = getEnumAttribute<BlockImagesAlign>(node.properties, 'align', [
        'center',
        'left',
        'right',
    ]);
    const data: BlockImagesData = {
        ...(align ? { align } : undefined),
        ...(typeof node.properties?.dataFullWidth !== 'undefined'
            ? { fullWidth: getBooleanAttribute(node.properties, 'dataFullWidth') }
            : undefined),
        ...(typeof node.properties?.dataWithFrame !== 'undefined'
            ? { withFrame: getBooleanAttribute(node.properties, 'dataWithFrame') }
            : undefined),
    };

    return [
        Block.create({
            type: BLOCK_IMAGES,
            nodes: images,
            data,
        }),
    ];
}
